import SortDirection from '@rio-cloud/rio-uikit/SortDirection';
import { endOfMonth, format, startOfYear } from 'date-fns';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useInvoices } from '@/api/invoices';
import type { InvoiceType } from '@/api/schema';
import { type Column, Table } from '@/components/Table/Table';
import { TimePicker, timeFramePickerLabel } from '@/components/TimePicker';
import { DateFrame, type TimeFrame, TimePickerType } from '@/components/TimePicker/types';
import SupportModal from '@/features/chargingActivity/components/SupportModal';
import { columns } from '@/features/invoices/columns';
import { EInvoicingAlert } from '@/features/invoices/components/EInvoicingAlert';
import { InvoicesTotal } from '@/features/invoices/components/InvoicesTotal';
import { useUserStore } from '@/stores/userStore';

export const Invoices = () => {
    const intl = useIntl();
    const { locale } = intl;
    const currentDate = new Date();
    const { currency, eInvoicingDetails } = useUserStore();
    const cacheColumns = useMemo<Column<InvoiceType>[]>(
        () => columns(intl, currency, locale),
        [intl, currency, locale]
    );

    const [timeFrame, setTimeFrame] = useState<TimeFrame>({
        startDate: startOfYear(currentDate),
        endDate: endOfMonth(currentDate),
        frame: {
            timeEnum: DateFrame.YEAR,
            timeLabel: timeFramePickerLabel(DateFrame.YEAR, intl),
        },
    });

    const { data: invoices, isLoading } = useInvoices({
        startDate: format(timeFrame.startDate ?? new Date(), 'yyyy-MM-dd'),
        endDate: format(timeFrame.endDate ?? new Date(), 'yyyy-MM-dd'),
    });

    const handleTimeFrameChange = (newTimeFrame: TimeFrame) => {
        setTimeFrame(newTimeFrame);
    };

    const hasEInvoicing = eInvoicingDetails && eInvoicingDetails.length > 0;

    const renderToolbar = () => (
        <div className='display-flex flex-1-1-0 align-items-end flex-wrap justify-content-between margin-top-10 padding-top-2'>
            <TimePicker
                disabled={isLoading}
                timePickerType={TimePickerType.LONG}
                timeFrame={timeFrame}
                setTimeFrame={handleTimeFrameChange}
                callback={(startDate?: Date, endDate?: Date) => {
                    if (startDate && endDate) {
                        setTimeFrame(prev => ({
                            ...prev,
                            startDate,
                            endDate,
                        }));
                    }
                }}
                selectByMonth
            />
            <SupportModal />
        </div>
    );

    return (
        <>
            {hasEInvoicing && <EInvoicingAlert />}
            <Table
                data={invoices?.invoices.slice().reverse() ?? []}
                columns={cacheColumns}
                isLoading={isLoading}
                searchByLabel={intl.formatMessage({ id: 'invoices.invoiceNumber' })}
                noMoreMessage={intl.formatMessage({ id: 'invoices.everythingIsLoaded' })}
                noResults={{
                    title: intl.formatMessage({ id: 'invoices.noResultsFound.title' }),
                    description: intl.formatMessage({ id: 'invoices.noResultsFound.description' }),
                }}
                nonFilterableColumns={['issueDate', 'dueDate', 'totalCost']}
                disableRowSelection
                overview={<InvoicesTotal timeFrame={timeFrame} isLoading={isLoading} currency={currency} />}
                showFilter
                extraFilters={renderToolbar()}
                initialSortKey='issueDate'
                initialSortDirection={SortDirection.DESCENDING}
                getRowKey={(row, idx) => row.invoiceNumber ?? idx}
            />
        </>
    );
};
